import { useEffect, useState } from 'react';
import { db, withSyncMeta } from '@/offline/db';
import { enqueueSync } from '@/offline/sync';
import { useAuthStore } from '@/lib/auth-store';
import { supabase } from '@/lib/supabase';
import type { EPCR, Narrative, VitalSet, Procedure, MedicationGiven } from '@/types';
import { cn } from '@/lib/cn';
import { FileText, Sparkles, Lock, Pencil, Check, Loader2, AlertTriangle, RefreshCw, ShieldCheck } from 'lucide-react';
import { DemoFillButton } from '@/components/DemoFillButton';
import { demoNarrative } from '@/lib/demo-data';
import { auditNarrative } from '@/lib/narrative-quality';
import { generateNarrativeFromEpcr } from '@/modules/narrative/v2';
import { activeProtocols } from '@/lib/care-matrix';

interface Props {
  epcr: EPCR;
  onUpdate: (updates: Partial<EPCR>) => void;
}

export function NarrativeTab({ epcr, onUpdate }: Props) {
  const { user, hasRole } = useAuthStore();
  const [narrative, setNarrative] = useState<Narrative | null>(null);
  const [editText, setEditText] = useState('');
  const [editing, setEditing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadNarrative();
  }, [epcr.id]);

  async function loadNarrative() {
    const records = await db.narratives
      .where('epcr_id')
      .equals(epcr.id)
      .toArray();
    if (records.length > 0) {
      setNarrative(records[0]);
      setEditText(records[0].edited_text ?? records[0].generated_text);
    }
  }

  async function generateNarrative() {
    setGenerating(true);
    setError(null);

    try {
      // PRIMARY PATH — local v2 narrative engine.
      //
      // The narrative is built deterministically from the structured
      // ePCR record (medications_given / procedures / vital_sets +
      // ePCR free-text fields). Every Care Matrix tap participates;
      // the narrative reflects the current state of the report. Runs
      // entirely client-side, works offline, and IS the same engine
      // the protocol.emspcr.app sandbox uses for dry-run previews —
      // ported in so the production narrative comes from inside the
      // report, not from outside it.
      const protocolNames = activeProtocols(epcr).map((p) => `${p.id} ${p.name}`);
      const providerLevel = ((): 'EMT' | 'AEMT' | 'AEMT-Cardiac' | 'Paramedic' => {
        const c = (user?.cert_level ?? '').toString().toUpperCase();
        if (/PARAMEDIC|EMT-P\b/.test(c)) return 'Paramedic';
        if (/CARDIAC/.test(c)) return 'AEMT-Cardiac';
        if (/AEMT|EMT-I/.test(c)) return 'AEMT';
        return 'EMT';
      })();
      const draft = await generateNarrativeFromEpcr(epcr, { providerLevel, protocolNames });
      await saveNarrative(draft.fullText);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate narrative');
    } finally {
      setGenerating(false);
    }
  }

  async function saveNarrative(text: string) {
    const existing = narrative;

    if (existing) {
      const updated: Narrative = {
        ...existing,
        generated_text: text,
        edited_text: null,
        version: existing.version + 1,
        updated_at: new Date().toISOString(),
      };
      setNarrative(updated);
      setEditText(text);
      await db.narratives.update(existing.id, { ...updated, _dirty: true, _last_modified: new Date().toISOString() });
      await enqueueSync('narratives', existing.id, 'update', updated as unknown as Record<string, unknown>);
    } else {
      const newNarrative: Narrative = {
        id: crypto.randomUUID(),
        epcr_id: epcr.id,
        generated_text: text,
        edited_text: null,
        version: 1,
        approved: false,
        approved_by: null,
        approved_at: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      await db.narratives.add(withSyncMeta(newNarrative));
      await enqueueSync('narratives', newNarrative.id, 'insert', newNarrative as unknown as Record<string, unknown>);
      setNarrative(newNarrative);
      setEditText(text);
    }
  }

  async function saveEdits() {
    if (!narrative) return;
    const updated = { ...narrative, edited_text: editText, updated_at: new Date().toISOString() };
    setNarrative(updated);
    setEditing(false);
    await db.narratives.update(narrative.id, { edited_text: editText, _dirty: true, _last_modified: new Date().toISOString() });
    await enqueueSync('narratives', narrative.id, 'update', updated as unknown as Record<string, unknown>);
  }

  async function approveNarrative() {
    if (!narrative || !user) return;
    const updated = {
      ...narrative,
      approved: true,
      approved_by: user.id,
      approved_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    setNarrative(updated);
    onUpdate({ narrative_approved: true });
    await db.narratives.update(narrative.id, {
      approved: true, approved_by: user.id, approved_at: updated.approved_at,
      _dirty: true, _last_modified: new Date().toISOString(),
    });
    await enqueueSync('narratives', narrative.id, 'update', updated as unknown as Record<string, unknown>);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-slate-200">Clinical Narrative</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            RI Protocol 1.02 format selected below. The narrative engine assembles
            a draft from your structured captures; you verify, edit, and approve.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <DemoFillButton
            label="Fill demo narrative"
            onFill={() => { setEditText(demoNarrative()); setEditing(true); }}
          />
          {!epcr.locked && (
            <button
              onClick={generateNarrative}
              disabled={generating}
              className="btn-primary gap-1"
            >
              {generating ? (
                <><Loader2 size={16} className="animate-spin" /> Generating...</>
              ) : narrative ? (
                <><RefreshCw size={16} /> Regenerate</>
              ) : (
                <><Sparkles size={16} /> Generate Narrative</>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Format picker — RI Protocol 1.02. Persisted on the ePCR so
          re-generations honor the choice. Defaults to DCHARTE. */}
      <NarrativeFormatPicker
        value={epcr.narrative_format ?? 'DCHARTE'}
        disabled={epcr.locked}
        onChange={(f) => onUpdate({ narrative_format: f })}
      />


      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-800 bg-red-950 p-3 text-sm text-red-400">
          <AlertTriangle size={16} />
          {error}
        </div>
      )}

      {narrative ? (
        <div className="space-y-4">
          {/* Narrative display/editor */}
          {editing ? (
            <div className="space-y-3">
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                className="input-field min-h-[20rem] resize-y font-mono text-sm leading-relaxed"
              />
              <div className="flex gap-2">
                <button onClick={saveEdits} className="btn-primary gap-1">
                  <Check size={16} /> Save Edits
                </button>
                <button onClick={() => { setEditing(false); setEditText(narrative.edited_text ?? narrative.generated_text); }} className="btn-secondary">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div
              className="rounded-lg border border-slate-700 bg-slate-800 p-4 text-field leading-relaxed text-slate-200 whitespace-pre-wrap"
            >
              {narrative.edited_text ?? narrative.generated_text}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <span>Version {narrative.version}</span>
              {narrative.edited_text && <span className="text-amber-400">Manually edited</span>}
              {narrative.approved && (
                <span className="flex items-center gap-1 text-ems-green">
                  <Check size={14} /> Approved
                </span>
              )}
            </div>
            {!epcr.locked && (
              <div className="flex gap-2">
                {!editing && !narrative.approved && (
                  <button onClick={() => setEditing(true)} className="btn-secondary gap-1 text-sm">
                    <Pencil size={14} /> Edit
                  </button>
                )}
                {!narrative.approved && (
                  <button onClick={approveNarrative} className="btn-primary gap-1 text-sm">
                    <Check size={14} /> Approve Narrative
                  </button>
                )}
              </div>
            )}
          </div>

          {/* § 11 two-layer audit — syntax + coverage */}
          <NarrativeQualityPanel text={editing ? editText : (narrative.edited_text ?? narrative.generated_text)} epcr={epcr} />

          {/* Info */}
          <p className="text-xs text-slate-600">
            No patient identifiers are sent to the narrative engine. Names are replaced with
            "the patient" during prompt construction.
          </p>
        </div>
      ) : (
        <div className="rounded-sm border border-slate-700 bg-slate-900 py-8 px-4 text-center">
          <FileText size={40} className="mx-auto mb-2 text-slate-600" />
          <p className="text-slate-400">No narrative yet</p>
          <p className="text-sm text-slate-500 mt-1">
            Tap <strong>Generate Narrative</strong> to auto-write from your documented data,
            or write one manually.
          </p>
          {!epcr.locked && (
            <button
              onClick={async () => {
                await saveNarrative('');
                setEditing(true);
              }}
              className="btn-secondary gap-1 text-sm mt-4"
            >
              <Pencil size={14} /> Write Manually
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function NarrativeQualityPanel({ text, epcr }: { text: string; epcr: EPCR }) {
  const findings = auditNarrative(text, epcr);
  const warnCount = findings.filter((f) => f.level === 'warn').length;

  if (findings.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-emerald-800 bg-emerald-950/40 p-3 text-sm text-emerald-300">
        <ShieldCheck size={14} />
        Narrative passes syntax + coverage audit.
      </div>
    );
  }

  return (
    <div className={cn(
      'rounded-lg border p-3 space-y-2 text-sm',
      warnCount > 0 ? 'border-amber-800 bg-amber-950/30 text-amber-200' : 'border-slate-700 bg-slate-900 text-slate-300',
    )}>
      <div className="flex items-center gap-2 font-semibold">
        <ShieldCheck size={14} />
        Narrative audit · {findings.length} finding{findings.length === 1 ? '' : 's'}
        {warnCount > 0 && <span className="text-xs font-normal">({warnCount} warning{warnCount === 1 ? '' : 's'})</span>}
      </div>
      <ul className="space-y-1 text-xs">
        {findings.map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className={cn(
              'mt-1 h-1.5 w-1.5 rounded-full shrink-0',
              f.level === 'warn' ? 'bg-amber-400' : 'bg-slate-500',
            )} />
            <div>
              <span className="font-semibold">{f.label}</span>
              <span className="text-slate-400"> — {f.detail}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Dev mode narrative generator (no API key needed)
function generateDevNarrative(
  epcr: EPCR,
  vitals: VitalSet[],
  procedures: Procedure[],
  medications: MedicationGiven[],
): string {
  const parts: string[] = [];

  parts.push(`Unit dispatched for ${epcr.dispatch_reason ?? 'a medical emergency'} at the reported address.`);

  if (epcr.chief_complaint) {
    parts.push(`Upon arrival, the patient presented with a chief complaint of ${epcr.chief_complaint.toLowerCase()}.`);
  }

  if (epcr.primary_impression) {
    parts.push(`Primary impression: ${epcr.primary_impression}.`);
  }

  if (epcr.medical_history.length > 0 && epcr.medical_history[0] !== 'None') {
    parts.push(`Patient medical history is significant for ${epcr.medical_history.join(', ')}.`);
  }

  if (vitals.length > 0) {
    const v = vitals[0];
    const vitalParts: string[] = [];
    if (v.bp_systolic && v.bp_diastolic) vitalParts.push(`BP ${v.bp_systolic}/${v.bp_diastolic}`);
    if (v.heart_rate) vitalParts.push(`HR ${v.heart_rate}`);
    if (v.respiratory_rate) vitalParts.push(`RR ${v.respiratory_rate}`);
    if (v.spo2) vitalParts.push(`SpO2 ${v.spo2}%`);
    if (vitalParts.length > 0) {
      parts.push(`Initial vital signs: ${vitalParts.join(', ')}.`);
    }
  }

  if (procedures.length > 0) {
    const procText = procedures
      .filter((p) => p.outcome === 'successful')
      .map((p) => p.procedure_type.toLowerCase())
      .join(', ');
    if (procText) parts.push(`Interventions performed: ${procText}.`);
  }

  if (medications.length > 0) {
    const medText = medications
      .map((m) => `${m.drug_name} ${m.dose} ${m.unit} ${m.route}`)
      .join('; ');
    parts.push(`Medications administered: ${medText}.`);
  }

  if (epcr.transport_decision === 'transport_this_unit' && epcr.destination_hospital) {
    parts.push(`Patient transported to ${epcr.destination_hospital} without incident. Care transferred to receiving staff with verbal and written report.`);
  } else if (epcr.transport_decision === 'refusal_care' || epcr.transport_decision === 'refusal_transport') {
    parts.push(`Patient was informed of the risks of refusing medical care/transport. Patient demonstrated capacity to make informed decisions and signed a refusal form.`);
  }

  return parts.join('\n\n');
}

/**
 * NarrativeFormatPicker — RI Protocol 1.02 format selector.
 * Four-button toggle group; persists onto the ePCR so re-generations
 * honor the choice and the audit log records which format was used.
 */
type NarrativeFormat = 'DCHARTE' | 'SBAR' | 'SOAP' | 'DISPATCH';

const FORMAT_BLURBS: Record<NarrativeFormat, string> = {
  DCHARTE:  'Dispatch · Complaint · History · Assessment · Rx/Treatment · Transport · Exception',
  SBAR:     'Situation · Background · Assessment · Treatment',
  SOAP:     'Subjective · Objective · Assessment · Plan',
  DISPATCH: 'Dispatch · Impression · Scene · Statements · Assessment · Treatment · Changes · Handoff',
};

function NarrativeFormatPicker({
  value, disabled, onChange,
}: {
  value: NarrativeFormat;
  disabled: boolean;
  onChange: (v: NarrativeFormat) => void;
}) {
  const formats: NarrativeFormat[] = ['DCHARTE', 'SBAR', 'SOAP', 'DISPATCH'];
  return (
    <div className="border border-slate-700 rounded p-3 bg-slate-900/40 space-y-2">
      <div className="flex items-baseline gap-2">
        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
          Narrative format
        </span>
        <span className="text-[10px] text-slate-600">
          (RI Protocol 1.02)
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
        {formats.map((f) => {
          const active = value === f;
          return (
            <button
              key={f}
              type="button"
              disabled={disabled}
              onClick={() => onChange(f)}
              className={cn(
                'rounded-md border px-3 py-2 text-sm font-bold min-h-touch transition-colors',
                active
                  ? 'bg-blue-700 border-blue-800 text-white'
                  : 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700',
              )}
            >
              {f}
            </button>
          );
        })}
      </div>
      <p className="text-[11px] text-slate-500 leading-snug">
        {FORMAT_BLURBS[value]}
      </p>
    </div>
  );
}
